import React, { useId } from "react";
import styled from "styled-components";
import { bodyType, captionType, colorTransition } from "./base/mixins";

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
	helperText?: React.ReactNode;
	error?: boolean;
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
	({ helperText, error = false, className, id, rows = 4, ...rest }, ref) => {
		const generated = useId();
		const textareaId = id ?? generated;
		const helperId = helperText ? `${textareaId}-helper` : undefined;

		return (
			<Field className={className}>
				<StyledTextarea
					ref={ref}
					id={textareaId}
					rows={rows}
					$error={error}
					aria-invalid={error || undefined}
					aria-describedby={helperId}
					{...rest}
				/>
				{helperText ? (
					<Helper id={helperId} $error={error}>
						{helperText}
					</Helper>
				) : null}
			</Field>
		);
	},
);

Textarea.displayName = "Textarea";

const Field = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
`;

const StyledTextarea = styled.textarea<{ $error: boolean }>`
	width: 100%;
	padding: var(--space-4);
	border: var(--border-default);
	border-color: ${({ $error }) => ($error ? "var(--color-danger)" : "var(--color-border)")};
	border-radius: var(--radius-md);
	background: var(--color-surface);
	box-shadow: none;
	resize: vertical;
	${bodyType}
	${colorTransition}

	&::placeholder {
		color: var(--color-text-secondary);
	}
	&:hover {
		border-color: ${({ $error }) =>
			$error ? "var(--color-danger)" : "var(--color-border-strong)"};
	}
	/* §9: a field shows focus on its own border, not with a ring around it. */
	&:focus-visible {
		outline: none;
		border-color: ${({ $error }) => ($error ? "var(--color-danger)" : "var(--focus-border)")};
	}
	&:disabled {
		opacity: 0.45;
		cursor: not-allowed;
	}
`;

const Helper = styled.span<{ $error: boolean }>`
	margin-top: var(--space-1);
	${captionType}
	color: ${({ $error }) => ($error ? "var(--color-danger)" : "var(--color-text-secondary)")};
`;

export default Textarea;
